export function ShimmerBlock({ className }: { className?: string }) {
  return <div aria-hidden className={`animate-pulse rounded-sm bg-grey-200 ${className ?? ""}`} />;
}

export function TableCardSkeleton({ rows = 5 }: { rows?: number }) {
  return (
    <section className="card flex flex-1 flex-col gap-4">
      <header className="flex flex-col gap-1">
        <ShimmerBlock className="h-5 w-48" />
        <ShimmerBlock className="h-4 w-64" />
      </header>
      <div className="flex flex-col overflow-hidden rounded-sm border border-grey-200 bg-white shadow-flat">
        <div className="flex h-9 items-center gap-4 border-b border-grey-200 bg-grey-50 px-2">
          <ShimmerBlock className="h-3 w-20" />
          <ShimmerBlock className="h-3 w-16" />
          <ShimmerBlock className="h-3 w-12" />
        </div>
        {Array.from({ length: rows }).map((_, i) => (
          <div key={i} className="flex h-12 items-center gap-4 border-b border-grey-100 px-2">
            <ShimmerBlock className="h-4 max-w-45 flex-1" />
            <ShimmerBlock className="h-4 w-16" />
            <ShimmerBlock className="h-4 w-10" />
          </div>
        ))}
      </div>
    </section>
  );
}

export function StatCardSkeleton() {
  return (
    <article className="card flex flex-col gap-3">
      <header className="flex items-start justify-between gap-3">
        <ShimmerBlock className="size-8" />
        <ShimmerBlock className="h-5 w-20" />
      </header>
      <ShimmerBlock className="h-9 w-24" />
      <ShimmerBlock className="h-4 w-28" />
      <div aria-hidden className="h-px w-full bg-grey-200" />
      <ShimmerBlock className="h-3 w-24" />
    </article>
  );
}

export function ChartCardSkeleton() {
  return (
    <article className="card flex flex-col gap-4">
      <header className="flex items-start justify-between gap-2">
        <div className="flex flex-col gap-1">
          <ShimmerBlock className="h-5 w-40" />
          <ShimmerBlock className="h-4 w-56" />
        </div>
        <ShimmerBlock className="h-7 w-28" />
      </header>
      <ShimmerBlock className="h-[229px] w-full" />
    </article>
  );
}

export function DashboardSkeleton({
  monitoring,
  screening,
}: {
  monitoring: boolean;
  screening: boolean;
}) {
  const monitoringOnly = monitoring && !screening;
  const kpiCount = (screening ? 2 : 0) + (monitoring ? 2 : 0);
  return (
    <>
      <div className="@container">
        <section className={`grid grid-cols-1 gap-2 ${gridColsForCount(kpiCount)}`}>
          {Array.from({ length: kpiCount }).map((_, i) => (
            <StatCardSkeleton key={i} />
          ))}
        </section>
      </div>
      {monitoringOnly ? (
        <section className="grid grid-cols-1 gap-2 lg:grid-cols-2">
          <div className="flex flex-col gap-2">
            <ChartCardSkeleton />
            <ChartCardSkeleton />
          </div>
          <TableCardSkeleton rows={6} />
        </section>
      ) : (
        <>
          <section
            className={`grid grid-cols-1 gap-2 ${screening && monitoring ? "lg:grid-cols-2" : ""}`}
          >
            {screening && <ChartCardSkeleton />}
            {monitoring && <ChartCardSkeleton />}
          </section>
          <section
            className={`grid grid-cols-1 gap-2 ${screening && monitoring ? "lg:grid-cols-2" : ""}`}
          >
            {screening && <TableCardSkeleton rows={5} />}
            {monitoring && <TableCardSkeleton rows={5} />}
          </section>
        </>
      )}
    </>
  );
}

export function gridColsForCount(n: number): string {
  if (n >= 4) return "@md:grid-cols-2 @4xl:grid-cols-4";
  if (n === 3) return "@md:grid-cols-3";
  if (n === 2) return "@md:grid-cols-2";
  return "";
}
